import { Modal, Input } from 'antd';
import { QuestionOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { TeambinderAiApiService } from '@src/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import KeywordDefinitionHelpModal from './KeywordDefinitionHelpModal';

const { TextArea } = Input;
const { confirm, info } = Modal;

interface KeywordDefinitionModalProps {
  visible: boolean;
  keyword: string;
  keywordId: string;
  onClose: () => void;
  onModify: () => void;
}

const KeywordDefinitionModal = ({
  visible,
  keyword,
  keywordId,
  onClose,
  onModify,
}: KeywordDefinitionModalProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: keywordContext, isLoading } = useQuery({
    queryKey: ['keywordContext', keywordId],
    queryFn: () => TeambinderAiApiService.getKeywordContext({ keywordId }),
    enabled: visible && !!keywordId,
    useErrorBoundary: false,
    suspense: false,
  });

  useEffect(() => {
    if (keywordContext?.data) {
      setEditedContent(keywordContext.data);
    }
  }, [keywordContext?.data]);

  const updateKeywordContext = useMutation({
    mutationFn: (content: string) =>
      TeambinderAiApiService.modifyKeywordContext1({
        keywordId,
        requestBody: {
          keywordContext: content,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['keywordContext', keywordId]);
      setIsEditMode(false);
      info({
        title: '수정되었습니다.',
        okText: '확인',
      });
    },
  });

  const handleSave = () => {
    updateKeywordContext.mutate(editedContent);
  };

  const handleModify = () => {
    if (isEditMode) {
      handleSave();
    } else {
      setIsEditMode(true);
    }
  };

  const handleClose = () => {
    if (isEditMode) {
      confirm({
        title: '지금 나가면 입력 중인 내용이 저장되지 않습니다.',
        okText: '계속 수정하기',
        cancelText: '나가기',
        onOk() {
          // 계속 수정하기
        },
        onCancel() {
          setIsEditMode(false);
          setEditedContent(keywordContext?.data || '');
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={
        <div
          style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            padding: '16px 0',
          }}
        >
          {isEditMode ? (
            <button
              onClick={handleModify}
              disabled={updateKeywordContext.isLoading}
              style={{
                padding: '12px 48px',
                backgroundColor: '#4403B5',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                opacity: updateKeywordContext.isLoading ? 0.7 : 1,
              }}
            >
              {updateKeywordContext.isLoading ? '저장 중...' : '저장'}
            </button>
          ) : (
            <>
              <button
                onClick={handleModify}
                style={{
                  padding: '12px 48px',
                  backgroundColor: '#fff',
                  color: '#4403B5',
                  border: '1px solid #4403B5',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                수정
              </button>
              <button
                onClick={handleClose}
                style={{
                  padding: '12px 48px',
                  backgroundColor: '#4403B5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                확인
              </button>
            </>
          )}
        </div>
      }
      width={800}
      centered
      bodyStyle={{
        maxHeight: 'calc(100vh - 250px)',
        overflowY: 'auto',
        paddingBottom: 0,
      }}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ color: '#4403B5', fontSize: '20px', fontWeight: 700 }}>
            {keyword}
          </span>
          <span style={{ color: '#666', fontSize: '20px', fontWeight: 700 }}>
            키워드 정의
          </span>
          <button
            onClick={() => setShowHelpModal(true)}
            style={{
              padding: '6px 12px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              e.currentTarget.style.borderColor = '#b3b3b3';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#d9d9d9';
            }}
          >
            <QuestionOutlined style={{ fontSize: '12px' }} />
            도움말
          </button>
        </div>
      }
    >
      <div>
        <div style={{ marginBottom: '20px' }}>
          <ul
            style={{
              listStyle: 'disc',
              paddingLeft: '20px',
              fontSize: '14px',
              lineHeight: '1.8',
              color: '#666',
            }}
          >
            <li>
              키워드 정의란 어떤 단어(키워드)가 여러 가지 의미로 쓰일 수 있을
              때, 우리가 원하는 의미로 정확하게 지정해주는 기능입니다.
            </li>
            <li>예시 키워드 : 토스</li>
            <li>
              뜻이 여러 개 : (1) 은행 앱 토스 (2) 배구 토스 (3) 어떤걸 넘기다
            </li>
            <li>우리가 원하는 정의 : "모바일 송금 및 금융 서비스 토스"</li>
            <li>
              검색 예시 ⇒ "모바일 송금 및 금융 서비스 토스에 대해 정의 내려줘"
            </li>
            <li>아래 사이트에 방문 후 검색하여 결과를 넣어주세요.</li>
            <li>
              <a href="https://chatgpt.com" target="_blank" rel="noreferrer">
                OpenAI
              </a>
              ,&nbsp;
              <a
                href="https://gemini.google.com/app?hl=ko"
                target="_blank"
                rel="noreferrer"
              >
                Gemini
              </a>
              ,&nbsp;
              <a
                href="https://www.perplexity.ai/"
                target="_blank"
                rel="noreferrer"
              >
                Perplexity
              </a>
              ,&nbsp;
              <a
                href="https://chat.deepseek.com/"
                target="_blank"
                rel="noreferrer"
              >
                Deepseek
              </a>
            </li>
          </ul>
        </div>

        <div
          style={{
            borderTop: '1px solid #e0e0e0',
            paddingTop: '32px',
          }}
        >
          {isLoading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>로딩 중...</p>
          ) : isEditMode ? (
            <TextArea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={10}
              style={{
                fontSize: '16px',
                lineHeight: '1.8',
                resize: 'vertical',
              }}
            />
          ) : keywordContext?.data ? (
            <div
              style={{
                fontSize: '16px',
                lineHeight: '1.8',
                color: '#333',
                whiteSpace: 'pre-wrap',
              }}
            >
              {keywordContext.data}
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#666' }}>
              키워드 정의 데이터 작업중입니다.
              <br />
              5~10분 뒤 데이터 확인 가능합니다.
            </p>
          )}
        </div>
      </div>

      {showHelpModal && (
        <KeywordDefinitionHelpModal
          visible={showHelpModal}
          onClose={() => setShowHelpModal(false)}
        />
      )}
    </Modal>
  );
};

export default KeywordDefinitionModal;
